import { createSignal, onCleanup, onMount } from 'solid-js';
import { Duration } from 'effect';

import {
  customShadowlessElement,
  type CorrectComponentType,
  correctElementType,
} from '@brytdesigns/web-component-core/utils';

type Props = {
  /** Hour of the day (New York time, 0-23) that same-day shipping stops. */
  cutoffHour: number;
};

const formatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  hourCycle: 'h23',
});

function easternTime(at: number): Date {
  const p: Record<string, number> = {};
  for (const part of formatter.formatToParts(at)) {
    if (part.type !== 'literal') {
      p[part.type] = parseInt(part.value, 10);
    }
  }
  return new Date(Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second));
}

/** Real UTC timestamp of the next cutoff, rolling to the next day once it has passed. */
function getCutoff(cutoffHour: number): number {
  const now = Date.now();
  const wall = easternTime(now);

  // New York's current offset from UTC, in ms (e.g. -4h during EDT).
  const offset = wall.getTime() - Math.floor(now / 1000) * 1000;

  const target = new Date(wall);
  target.setUTCHours(cutoffHour, 0, 0, 0);

  if (wall.getTime() >= target.getTime()) {
    target.setUTCDate(target.getUTCDate() + 1);
  }

  return target.getTime() - offset;
}

function createCountdown(cutoffHour: () => number) {
  const [remaining, setRemaining] = createSignal<string | null>(null);

  onMount(() => {
    let timeout: ReturnType<typeof setTimeout>;

    const tick = () => {
      const diffMs = getCutoff(cutoffHour()) - Date.now();

      setRemaining(Duration.format(Duration.minutes(Math.floor(diffMs / 60_000))));

      // Land on the boundary where the displayed value actually changes, so the
      // countdown never drifts out of phase or skips a second.
      timeout = setTimeout(tick, diffMs % 1000 || 1000);
    };

    tick();

    onCleanup(() => clearTimeout(timeout));
  });

  return remaining;
}

const Component: CorrectComponentType<Props> = (props) => {
  const remaining = createCountdown(() => Number(props.cutoffHour));

  return <p class="text-sm text-sm--body mb-0">Order within <span class="font-bold text-heading">{remaining()}</span> for same day shipping in select areas.</p>;
};

customShadowlessElement('need-faster', { cutoffHour: 14 }, correctElementType(Component));
